import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch } from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { openDeleteModal } from "@components/DeleteModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { QP } from "@constants/index";

import { useGetPromoCodes } from "@hooks/queries/promocodes/useGetPromoCodes";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { PromoCode } from "@customTypes/promoCode";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddPromoCodeModal } from "./components/AddPromoCodeModal";
import { EditPromoCodeModal } from "./components/EditPromoCodeModal";


export const PromoCodesTab = () => {
  const { t, i18n } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });

  const [tableData, setTableData] = useState<PromoCode[]>([]);
  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [currentPromoCode, setCurrentPromoCode] =
    useState<PromoCode | null>(null);

  const [
    addPromoCodeModalOpened,
    { open: openAddPromoCodeModal, close: closeAddPromoCodeModal },
  ] = useDisclosure(false);

  const [
    editPromoCodeModalOpened,
    { open: openEditPromoCodeModal, close: closeEditPromoCodeModal },
  ] = useDisclosure(false);

  const { data, isLoading, isRefetching, fetchStatus, isError, isSuccess } = useGetPromoCodes({
    pagination: {
      page: tablePagination.page,
      pageSize: tablePagination.pageSize,
    },
    search: debounced.toLowerCase(),
  });

  // Promo code table rows (AdminDashboard pattern)
  const promoCodeRows = useMemo(() => {
    return tableData?.map(promoCode => (
      <Table.Tr key={promoCode.code}>
        <Table.Td>
          {(() => {
            const value = promoCode.code || "-";
            const isLong = value && value.length > 15;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={500}
                  lh={1.5}
                  style={{
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {promoCode.discountPercent}%
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {formatDateToString(promoCode.validFrom)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {formatDateToString(promoCode.validUntil)}
          </Text>
        </Table.Td>
        <Table.Td>
          {(() => {
            const value = promoCode.medicalProfessional || "-";
            const isLong = value && value.length > 20;
            return (
              <Tooltip label={isLong ? value : null} withArrow disabled={!isLong}>
                <Text
                  fz={14}
                  fw={400}
                  lh={1.5}
                  style={{
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {value}
                </Text>
              </Tooltip>
            );
          })()}
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="promo_codes.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentPromoCode(promoCode);
                  openEditPromoCodeModal();
                }}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [tableData, i18n.language]);

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data, setTablePagination]);

  useEffect(() => {
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length && !isMounted}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        />

        <div className="header">
          <Group w={"100%"} justify="space-between" align="center">
            <h1>{t("promo-codes")}</h1>
            <PermissionGate permission="promo_codes.create"><Button
              leftSection={<IconPlus size={16} />}
              onClick={openAddPromoCodeModal}>
              {t("add-promo-code")}
            </Button></PermissionGate>
          </Group>
        </div>

        <TextInput
          my={"md"}
          placeholder={t("search-for-a-promo-code")}
          leftSection={<IconSearch size={16} />}
          value={searchFilter || ""}
          onChange={event => setSearchFilter(event.currentTarget.value || null)}
          style={{ flex: 1, maxWidth: 400 }}
        />

        <div style={{ paddingBottom: "200px" }}>
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
            <Table
              ref={tableRef}
              withRowBorders={true}
              mt={"1rem"}
              miw={1280}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th>{t("code")}</Table.Th>
                  <Table.Th>{t("discount")}</Table.Th>
                  <Table.Th>{t("valid-from")}</Table.Th>
                  <Table.Th>{t("valid-until")}</Table.Th>
                  <Table.Th>{t("medical-professional")}</Table.Th>
                  <Table.Th>{t("action")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isLoading && fetchStatus === "fetching",
                  isError,
                  isSuccess,
                  dataLength: tableData.length,
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: ["md", "sm", "md", "md", "lg", "md"],
                      }}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={
                        debounced
                          ? t("no-promo-codes-found")
                          : t("no-promo-codes-available")
                      }
                    />
                  ),
                  dataComponent: promoCodeRows,
                })}
              </Table.Tbody>
            </Table>

            {/* Promo Codes Pagination */}
            {data && !(isLoading && fetchStatus === "fetching") && (
              <TablePagination
                paginationData={data?.pagination || {}}
                paginatedRows={data?.newData?.length || 0}
                onPageChange={(page) => setTablePagination(prev => ({ ...prev, page }))}
                isLoading={isLoading || fetchStatus === "fetching"}
              />
            )}
          </ScrollArea>
        </div>

        <PermissionGate permission="promo_codes.create"><AddPromoCodeModal
          opened={addPromoCodeModalOpened}
          close={closeAddPromoCodeModal}
        /></PermissionGate>

        <PermissionGate permission="promo_codes.edit"><EditPromoCodeModal
          opened={editPromoCodeModalOpened}
          close={closeEditPromoCodeModal}
          promoCode={currentPromoCode}
        /></PermissionGate>
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
